import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { Loader2 } from "lucide-react";

import type { MultiLevelDropdownProps } from "./types";

/**
 * Two-level menu: each group opens a scrollable submenu of checkable options.
 * - Options keep the menu open on select so several values can be ticked in a row.
 */
export const MultiLevelDropdown = <GroupKey extends string>({
  trigger,
  groups,
  values,
  onToggle,
  emptyLabel = "No options",
}: MultiLevelDropdownProps<GroupKey>) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-56">
      {groups.map((group) => {
        const selectedValues = values[group.key] ?? [];
        return (
          <DropdownMenuSub key={group.key}>
            <DropdownMenuSubTrigger>{group.label}</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-[min(20rem,var(--radix-dropdown-menu-content-available-height))] w-56 overflow-y-auto">
              {group.isLoading ? (
                <DropdownMenuItem disabled aria-busy="true" aria-live="polite">
                  <Loader2 className="animate-spin" />
                  Loading...
                </DropdownMenuItem>
              ) : group.options.length === 0 ? (
                <DropdownMenuItem disabled>{emptyLabel}</DropdownMenuItem>
              ) : (
                group.options.map((option) => (
                  <DropdownMenuCheckboxItem
                    key={option}
                    checked={selectedValues.includes(option)}
                    onCheckedChange={() => onToggle(group.key, option)}
                    onSelect={(event) => event.preventDefault()}
                  >
                    <span className="min-w-0 break-words">{option}</span>
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        );
      })}
    </DropdownMenuContent>
  </DropdownMenu>
);
